/** Shared Web Push helpers (Phase 5D). All best-effort; errors carry friendly messages. */
import api from "./api";

export function getPushEnv() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isStandalone =
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  return { isIOS, isStandalone, supported };
}

// "enabled" | "off" | "denied" | "ios_install" | "unsupported"
export async function getPushState() {
  const env = getPushEnv();
  if (!env.supported) return env.isIOS && !env.isStandalone ? "ios_install" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    return sub ? "enabled" : "off";
  } catch {
    return "off";
  }
}

export async function enablePush() {
  const env = getPushEnv();
  if (env.isIOS && !env.isStandalone && !env.supported) {
    throw new Error("Add Venuefy to your Home Screen first, then enable alerts from there.");
  }
  if (!env.supported) throw new Error("This browser does not support background notifications.");
  const { data } = await api.get("/push/vapid-public-key");
  if (!data.available) throw new Error("Server push isn't configured yet — in-app alerts remain active.");
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications were not allowed — you'll still see in-app alerts.");
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    }));
  await api.post("/push/subscribe", { subscription: sub.toJSON(), user_agent: navigator.userAgent });
  return sub;
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (e) { console.debug("push: unsubscribe failed", e); }
  await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
}

export function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});
  }
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}
